import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { RoleCode, type RoleCode as RoleCodeType } from '@app/shared';
import { useAuth } from '../features/auth/use-auth';
import { InstitutionLogo, useInstitutionBrand } from './institution-brand';

type NavItem = {
  to: string;
  label: string;
  icon: (props: { className?: string }) => ReactNode;
  roles: RoleCodeType[];
  matches: (pathname: string) => boolean;
};

const nav = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: (props) => (
      <svg
        className={props.className}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
    roles: [RoleCode.ADMIN, RoleCode.GURU, RoleCode.KEPALA_SEKOLAH],
    matches: (pathname) => pathname === '/dashboard',
  },
  {
    to: '/absensi',
    label: 'Absensi',
    icon: (props) => (
      <svg
        className={props.className}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    roles: [RoleCode.ADMIN, RoleCode.GURU, RoleCode.KEPALA_SEKOLAH],
    matches: (pathname) => pathname === '/absensi',
  },
  {
    to: '/absensi/laporan',
    label: 'Laporan Absensi',
    icon: (props) => (
      <svg
        className={props.className}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
    roles: [RoleCode.ADMIN, RoleCode.GURU, RoleCode.KEPALA_SEKOLAH],
    matches: (pathname) => pathname.startsWith('/absensi/laporan'),
  },
  {
    to: '/honorarium/laporan',
    label: 'Laporan Honorarium',
    icon: (props) => (
      <svg
        className={props.className}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </svg>
    ),
    roles: [RoleCode.ADMIN, RoleCode.KEPALA_SEKOLAH],
    matches: (pathname) => pathname.startsWith('/honorarium/laporan'),
  },
  {
    to: '/honorarium',
    label: 'Honorarium',
    icon: (props) => (
      <svg
        className={props.className}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
    roles: [RoleCode.ADMIN],
    matches: (pathname) =>
      pathname.startsWith('/honorarium') && !pathname.startsWith('/honorarium/laporan'),
  },
  {
    to: '/master/pegawai',
    label: 'Daftar Pegawai',
    icon: (props) => (
      <svg
        className={props.className}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
    ),
    roles: [RoleCode.ADMIN, RoleCode.KEPALA_SEKOLAH],
    matches: (pathname) => pathname.startsWith('/master/pegawai'),
  },
  {
    to: '/pengaturan',
    label: 'Pengaturan',
    icon: (props) => (
      <svg
        className={props.className}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    roles: [RoleCode.ADMIN],
    matches: (pathname) =>
      pathname === '/pengaturan' ||
      pathname.startsWith('/master/sekolah') ||
      pathname.startsWith('/master/jabatan') ||
      pathname.startsWith('/master/honor-pegawai') ||
      pathname.startsWith('/master/jenis-komponen-honor') ||
      pathname.startsWith('/master/konfigurasi-honor-jabatan') ||
      pathname.startsWith('/absensi/periode') ||
      pathname.startsWith('/absensi/kalender') ||
      pathname.startsWith('/settings/institution') ||
      pathname.startsWith('/admin/akun') ||
      pathname.startsWith('/admin/pending-users') ||
      pathname.startsWith('/admin/audit-log') ||
      pathname.startsWith('/admin/pegawai-roles') ||
      pathname.startsWith('/admin/system-settings') ||
      pathname.startsWith('/admin/permissions'),
  },
  {
    to: '/profil-saya',
    label: 'Profil Saya',
    icon: (props) => (
      <svg
        className={props.className}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      </svg>
    ),
    roles: [RoleCode.ADMIN, RoleCode.GURU, RoleCode.KEPALA_SEKOLAH],
    matches: (pathname) => pathname.startsWith('/profil-saya') || pathname.startsWith('/slip-saya'),
  },
] satisfies NavItem[];

const roleLabels = {
  [RoleCode.ADMIN]: 'Admin',
  [RoleCode.GURU]: 'Guru',
  [RoleCode.KEPALA_SEKOLAH]: 'Kepala Sekolah',
} satisfies Record<RoleCodeType, string>;

const effectiveRolePriority = [
  RoleCode.ADMIN,
  RoleCode.KEPALA_SEKOLAH,
  RoleCode.GURU,
] satisfies RoleCodeType[];

export function getEffectiveRole(roles: RoleCodeType[] | undefined): RoleCodeType | null {
  if (!roles || roles.length === 0) return null;
  return effectiveRolePriority.find((role) => roles.includes(role)) ?? roles[0] ?? null;
}

function canSeeNav(item: NavItem, effectiveRole: RoleCodeType | null) {
  return effectiveRole ? item.roles.includes(effectiveRole) : false;
}

export function ApplicationShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const effectiveRole = getEffectiveRole(user?.roles);
  const { nama_pesantren } = useInstitutionBrand();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const title = pageTitle(location.pathname);

  useEffect(() => {
    setSidebarOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="app-shell">
      {sidebarOpen && (
        <button
          className="sidebar-overlay"
          aria-label="Tutup menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        className={`app-sidebar${sidebarOpen ? ' open' : ''}`}
        aria-label="Navigasi aplikasi"
        style={{
          overflowY: 'auto',
        }}
      >
        <div style={{ padding: '1.25rem 1rem', borderBottom: '1px solid #E2E8F0' }}>
          <Link
            to="/dashboard"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.625rem',
              color: '#0F172A',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: 700,
            }}
          >
            <InstitutionLogo size={36} />
            <span>{nama_pesantren}</span>
          </Link>
          <div style={{ marginTop: '0.375rem', color: '#64748B', fontSize: '0.75rem' }}>
            Sistem Absensi & Honorarium
          </div>
        </div>

        <nav aria-label="Menu utama" style={{ padding: '0.75rem 0' }}>
          {nav
            .filter((item) => canSeeNav(item, effectiveRole))
            .map((item) => {
              const active = item.matches(location.pathname);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={`app-nav-item${active ? ' active' : ''}`}
                >
                  <item.icon className="nav-icon" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
        </nav>
      </aside>

      <div style={{ minWidth: 0 }}>
        <header className="app-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', minWidth: 0 }}>
            <button
              type="button"
              className="mobile-menu-button"
              aria-label="Buka menu navigasi"
              aria-expanded={sidebarOpen}
              onClick={() => setSidebarOpen(true)}
              style={{
                width: 40,
                height: 40,
                border: '1px solid #CBD5E1',
                borderRadius: 6,
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              ☰
            </button>
            <div style={{ fontSize: '1rem', fontWeight: 700 }}>{title}</div>
          </div>
          <div ref={menuRef} style={{ position: 'relative', fontSize: '0.875rem' }}>
            <button
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              style={{
                background: '#FFFFFF',
                border: '1px solid #E2E8F0',
                color: '#0F172A',
                borderRadius: '0.375rem',
                minHeight: '36px',
                padding: '0 0.875rem',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              <span style={{ display: 'block', textAlign: 'left' }}>
                {user?.nama ?? 'Pengguna'} ▾
              </span>
              <span className="desktop-role" style={{ fontSize: '0.75rem', color: '#64748B' }}>
                {effectiveRole ? roleLabels[effectiveRole] : 'Pengguna'}
              </span>
            </button>
            {menuOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 0.5rem)',
                  minWidth: '180px',
                  padding: '0.375rem',
                  background: '#fff',
                  border: '1px solid #E2E8F0',
                  borderRadius: '0.375rem',
                  boxShadow: '0 8px 20px rgba(15,23,42,.12)',
                  zIndex: 20,
                }}
              >
                <Link
                  role="menuitem"
                  to="/profil-saya"
                  onClick={() => setMenuOpen(false)}
                  style={menuLinkStyle}
                >
                  Profil Saya
                </Link>
                <Link
                  role="menuitem"
                  to="/profil-saya#password"
                  onClick={() => setMenuOpen(false)}
                  style={menuLinkStyle}
                >
                  Ubah Password
                </Link>
                <button
                  role="menuitem"
                  onClick={handleLogout}
                  style={{
                    ...menuLinkStyle,
                    border: 0,
                    width: '100%',
                    color: '#B91C1C',
                    cursor: 'pointer',
                    background: 'transparent',
                    textAlign: 'left',
                  }}
                >
                  Keluar
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="app-main">
          <Breadcrumbs pathname={location.pathname} />
          {children}
        </main>
      </div>
    </div>
  );
}

export function AdminLayout({ title, children }: { title: string; children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const showBack = isDetailOrFormPage(pathname);

  const handleBack = () => {
    // Try to go back in history
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Fallback to parent module or main page if no history
      const fallback = getFallbackRoute(pathname);
      navigate(fallback);
    }
  };

  return (
    <section>
      <div
        style={{
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '.75rem',
          flexWrap: 'wrap',
        }}
      >
        {showBack && (
          <button
            type="button"
            onClick={handleBack}
            style={backButtonStyle}
            aria-label="Kembali ke halaman sebelumnya"
          >
            ← Kembali
          </button>
        )}
        <h1 style={{ fontSize: '1.75rem', lineHeight: 1.25, margin: 0, color: '#0F172A' }}>
          {title}
        </h1>
      </div>
      {children}
    </section>
  );
}

const menuLinkStyle: CSSProperties = {
  display: 'block',
  padding: '0.5rem 0.625rem',
  color: '#0F172A',
  textDecoration: 'none',
  borderRadius: '0.25rem',
  fontSize: '0.875rem',
};
const backButtonStyle: CSSProperties = {
  minHeight: 38,
  padding: '0 .75rem',
  border: '1px solid #CBD5E1',
  borderRadius: 6,
  background: '#fff',
  color: '#334155',
  cursor: 'pointer',
  fontWeight: 600,
};

const pathLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  absensi: 'Absensi',
  master: 'Master Data',
  pegawai: 'Daftar Pegawai',
  sekolah: 'Master Sekolah',
  jabatan: 'Master Jabatan',
  'honor-pegawai': 'Pengaturan Honor Pegawai',
  'jenis-komponen-honor': 'Jenis Komponen Honor',
  'konfigurasi-honor-jabatan': 'Konfigurasi Honor Jabatan',
  laporan: 'Laporan Absensi',
  periode: 'Periode Absensi',
  kalender: 'Kalender Kerja',
  pengaturan: 'Pengaturan',
  settings: 'Pengaturan',
  institution: 'Identitas Lembaga',
  'profil-saya': 'Profil Saya',
  honorarium: 'Honorarium',
  admin: 'Manajemen',
  'pending-users': 'Persetujuan Akun',
  akun: 'Manajemen Akun',
  slip: 'Slip Honorarium',
  'total-jp-mengajar': 'Total JP Mengajar',
  'audit-log': 'Audit Log',
  'system-settings': 'Pengaturan Sistem',
  permissions: 'Matriks Permission',
  baru: 'Tambah Data',
  edit: 'Edit Data',
};

function Breadcrumbs({ pathname }: { pathname: string }) {
  const parts = pathname.split('/').filter(Boolean);
  if (!parts.length) return null;
  const crumbs = parts.map((part, index) => ({
    label: /^\d+$/.test(part) ? 'Detail' : (pathLabels[part] ?? part),
    to: `/${parts.slice(0, index + 1).join('/')}`,
  }));
  return (
    <nav
      aria-label="Breadcrumb"
      style={{ marginBottom: '1rem', color: '#64748B', fontSize: '.875rem' }}
    >
      <Link to="/dashboard" style={{ color: '#475569' }}>
        Dashboard
      </Link>
      {crumbs
        .filter((crumb) => crumb.to !== '/dashboard')
        .map((crumb, index, array) => (
          <span key={crumb.to}>
            {' '}
            <span aria-hidden="true">›</span>{' '}
            {index === array.length - 1 ? (
              <span aria-current="page">{crumb.label}</span>
            ) : (
              <Link to={crumb.to} style={{ color: '#475569' }}>
                {crumb.label}
              </Link>
            )}
          </span>
        ))}
    </nav>
  );
}

function getFallbackRoute(pathname: string): string {
  // Fallback logic: navigate to parent module or main page

  // Master data pages → Pengaturan
  if (pathname.startsWith('/master/')) return '/pengaturan';

  // Settings pages → Pengaturan
  if (pathname.startsWith('/settings/')) return '/pengaturan';

  // Admin pages → Pengaturan
  if (pathname.startsWith('/admin/')) return '/pengaturan';

  // Absensi subpages → Dashboard (since Absensi is in main menu)
  if (pathname.startsWith('/absensi/')) return '/dashboard';

  // Honorarium pages → Dashboard
  if (pathname.startsWith('/honorarium/')) return '/dashboard';

  // Pengaturan page → Dashboard
  if (pathname.startsWith('/pengaturan')) return '/dashboard';

  // Default fallback → Dashboard
  return '/dashboard';
}

function isDetailOrFormPage(pathname: string) {
  // Main menu pages don't need back button (these are top-level navigation items)
  const mainMenuPages = [
    '/dashboard',
    '/absensi',
    '/absensi/laporan',
    '/honorarium/laporan',
    '/honorarium',
    '/master/pegawai',
    '/pengaturan',
    '/profil-saya',
  ];

  if (mainMenuPages.includes(pathname)) return false;

  // All other pages should have back button (submenus, forms, details)
  // This includes:
  // - Submenu pages under main menus
  // - All /master/* pages (sekolah, jabatan, honor-pegawai, insentif, etc.)
  // - All /absensi/* subpages (periode, kalender)
  // - All /honorarium/* subpages (slip, total-jp, draft, etc.)
  // - All /settings/* pages
  // - All /admin/* pages (except system-settings which is main menu)
  // - Form pages (baru, edit)
  // - Detail pages (with IDs)
  return true;
}

function pageTitle(pathname: string) {
  if (/\/baru$/.test(pathname)) return 'Tambah Data';
  if (/\/edit$/.test(pathname)) return 'Edit Data';
  if (pathname.startsWith('/master/honor-pegawai')) return 'Pengaturan Honor Pegawai';
  if (pathname.startsWith('/master/jenis-komponen-honor')) return 'Jenis Komponen Honor';
  if (pathname.startsWith('/master/konfigurasi-honor-jabatan')) return 'Konfigurasi Honor Jabatan';
  if (pathname.startsWith('/settings/institution')) return 'Identitas Lembaga';
  if (pathname.startsWith('/master/pegawai')) return 'Daftar Pegawai';
  if (pathname.startsWith('/master/sekolah')) return 'Master Sekolah';
  if (pathname.startsWith('/master/jabatan')) return 'Master Jabatan';
  if (pathname.startsWith('/absensi/laporan')) return 'Laporan Absensi';
  if (pathname.startsWith('/absensi/periode')) return 'Periode Absensi';
  if (pathname.startsWith('/absensi/kalender')) return 'Kalender Kerja';
  if (pathname.startsWith('/slip-saya')) return 'Slip Honorarium Saya';
  if (pathname.startsWith('/profil-saya')) return 'Profil Saya';
  if (pathname.startsWith('/honorarium/laporan')) return 'Laporan Honorarium';
  if (pathname.startsWith('/honorarium/total-jp-mengajar')) return 'Total JP Mengajar';
  if (pathname.startsWith('/honorarium')) return 'Honorarium';
  if (pathname.startsWith('/admin/audit-log')) return 'Audit Log';
  if (pathname.startsWith('/admin/system-settings')) return 'Pengaturan Sistem';
  if (pathname.startsWith('/admin/permissions')) return 'Matriks Permission';
  if (pathname.startsWith('/admin/pending-users')) return 'Persetujuan Akun';
  if (pathname.startsWith('/admin/akun')) return 'Manajemen Akun';
  if (pathname.startsWith('/pengaturan')) return 'Pengaturan';
  if (pathname.startsWith('/absensi')) return 'Absensi';
  return 'Dashboard';
}
